import React, { useState, useMemo } from 'react';
import styled from 'styled-components';
import { tieneCoordenadasValidas, filtrarClientesConCoordenadas } from '../utils/coordenadas';

const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
`;

const ModalContent = styled.div`
  background: white;
  border-radius: 8px;
  width: 95%;
  max-width: 100%;
  max-height: 100%;
  box-shadow: 0 5px 15px rgba(0, 0, 0, 0.3);
  display: flex;
  flex-direction: column;
`;

const ModalHeader = styled.div`
  padding: 1rem;
  border-bottom: 1px solid #dee2e6;
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const ModalTitle = styled.h2`
  margin: 0;
  font-size: 1.25rem;
  color: #2c3e50;
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  font-size: 1.5rem;
  cursor: pointer;
  color: #7f8c8d;
  
  &:hover {
    color: #34495e;
  }
`;

const ModalBody = styled.div`
  padding: 1rem;
  overflow-y: auto;
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 1rem;
`;

const SearchContainer = styled.div`
  display: flex;
  margin-bottom: 1rem;
  gap: 1rem;
  align-items: center;
`;

const SearchInput = styled.input`
  padding: 0.5rem 0.75rem;
  border: 1px solid #ced4da;
  border-radius: 4px;
  font-size: 1rem;
  flex: 1;
  max-width: 300px;
  
  &:focus {
    outline: none;
    border-color: #80bdff;
    box-shadow: 0 0 0 0.2rem rgba(0, 123, 255, 0.25);
  }
`;

const TableContainer = styled.div`
  overflow-x: auto;
  width: 100%;
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  margin-bottom: 1rem;
`;

const Th = styled.th`
  padding: 0.75rem;
  text-align: left;
  border-bottom: 2px solid #dee2e6;
  background-color: #f8f9fa;
`;

const SortableTh = styled(Th)`
  cursor: pointer;
  user-select: none;
  position: relative;
  padding-right: 1.5rem;
  
  &:hover {
    background-color: #e9ecef;
  }
  
  &::after {
    content: "";
    position: absolute;
    right: 0.5rem;
    top: 50%;
    width: 0;
    height: 0;
    border-left: 5px solid transparent;
    border-right: 5px solid transparent;
    border-top: 5px solid #495057;
    opacity: ${props => props.$sortDirection ? 1 : 0.3};
    transform: ${props => {
      const baseTransform = 'translateY(-50%)';
      const rotation = props.$sortDirection === 'asc' ? ' rotate(180deg)' : '';
      return baseTransform + rotation;
    }};
  }
`;

const Td = styled.td`
  padding: 0.75rem;
  border-bottom: 1px solid #dee2e6;
`;

const SummaryBox = styled.div`
  margin-bottom: 1.5rem;
`;

const SummaryTitle = styled.h3`
  margin: 0 0 1rem 0;
  font-size: 1.1rem;
  color: #2c3e50;
  font-weight: 600;
  text-align: center;
`;

const SummaryCardsContainer = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 1rem;
`;

const SummaryCard = styled.div`
  background-color: white;
  border-radius: 8px;
  padding: 1rem;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  text-align: center;
  display: flex;
  flex-direction: column;
  align-items: center;
  transition: transform 0.2s, box-shadow 0.2s;
  border-top: 4px solid ${props => props.color || '#3498db'};
  
  &:hover {
    transform: translateY(-3px);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  }
`;

const CardIcon = styled.div`
  font-size: 1.5rem;
  margin-bottom: 0.5rem;
  color: ${props => props.color || '#3498db'};
`;

const CardLabel = styled.div`
  font-size: 0.85rem;
  color: #7f8c8d;
  margin-bottom: 0.25rem;
  font-weight: 500;
`;

const CardValue = styled.div`
  font-size: 1.25rem;
  font-weight: 700;
  color: #2c3e50;
`;

const Label = styled.span`
  font-weight: 500;
`;

const Value = styled.span`
  font-weight: 600;
  color: #3498db;
`;

// Función para calcular la distancia entre dos puntos (en kilómetros)
const calcularDistancia = (lat1, lon1, lat2, lon2) => {
  const R = 6371; // Radio de la Tierra en km
  const dLat = (lat2 - lat1) * Math.PI / 180;
  const dLon = (lon2 - lon1) * Math.PI / 180;
  const a = 
    Math.sin(dLat/2) * Math.sin(dLat/2) +
    Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) * 
    Math.sin(dLon/2) * Math.sin(dLon/2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a));
  return R * c;
};

// Función para calcular la distancia total de la ruta
const calcularDistanciaTotal = (clientes) => {
  if (!clientes || clientes.length < 2) return 0;
  
  // Filtrar solo clientes con coordenadas válidas
  const clientesConCoordenadas = filtrarClientesConCoordenadas(clientes);
  
  if (clientesConCoordenadas.length < 2) return 0;
  
  let distanciaTotal = 0;
  
  for (let i = 0; i < clientesConCoordenadas.length - 1; i++) {
    const cliente1 = clientesConCoordenadas[i];
    const cliente2 = clientesConCoordenadas[i + 1];
    
    const distancia = calcularDistancia(
      cliente1.clt_lat,
      cliente1.clt_lon,
      cliente2.clt_lat,
      cliente2.clt_lon
    );
    
    distanciaTotal += distancia;
  }
  
  return distanciaTotal;
};

// Función para formatear valores en formato moneda
const formatoMoneda = (valor) => {
  if (valor === undefined || valor === null) return 'N/A';
  
  // Convertir a número si es string
  const numero = typeof valor === 'string' ? parseFloat(valor) : valor;
  
  // Formatear con punto como separador de miles y coma para decimales
  const partes = numero.toFixed(2).split('.');
  const entero = partes[0].replace(/\B(?=(\d{3})+(?!\d))/g, ".");
  const decimales = partes[1] || '00';
  
  return `$ ${entero},${decimales}`;
};

const formatoNumero = (valor) => {
  if (valor === undefined || valor === null) return 'N/A';
  
  // Convertir a número si es string
  const numero = typeof valor === 'string' ? parseFloat(valor) : valor;
  
  // Formatear con punto como separador de miles y coma para decimales
  const partes = numero.toFixed(2).split('.');
  const entero = partes[0].replace(/\B(?=(\d{3})+(?!\d))/g, ".");
  const decimales = partes[1] || '00';
  
  return `${entero},${decimales}`;
};

// Función para calcular la suma de un campo específico en un array de clientes
const calcularSuma = (clientes, campo) => {
  if (!clientes || clientes.length === 0) return 0;
  
  return clientes.reduce((suma, cliente) => {
    const valor = cliente[campo];
    return suma + (valor ? parseFloat(valor) : 0);
  }, 0);
};

const calcularSumaValCaj = (clientes, campo) => {
  if (!clientes || clientes.length === 0) return 0;
  
  return clientes.reduce((suma, cliente) => {
    const valor = cliente[campo];
    return suma + (valor ? parseFloat(valor) : 0)/clientes.length;
  }, 0);
};

const DetailModal = ({ isOpen, onClose, clientes }) => {
  if (!isOpen) return null;
  
  // Estados para búsqueda y ordenamiento
  const [searchTerm, setSearchTerm] = useState('');
  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });
  
  // Función para manejar la búsqueda
  const filteredClientes = useMemo(() => {
    if (!searchTerm) return clientes;
    
    const term = searchTerm.toLowerCase();
    return clientes.filter(cliente => 
      Object.values(cliente).some(value => 
        value && value.toString().toLowerCase().includes(term)
      )
    );
  }, [clientes, searchTerm]);
  
  // Función para manejar el ordenamiento
  const sortedClientes = useMemo(() => {
    const sortableItems = [...filteredClientes];
    
    if (sortConfig.key) {
      sortableItems.sort((a, b) => {
        let aValue = a[sortConfig.key];
        let bValue = b[sortConfig.key];
        
        // Manejar valores nulos o indefinidos
        if (aValue === null || aValue === undefined) return sortConfig.direction === 'asc' ? -1 : 1;
        if (bValue === null || bValue === undefined) return sortConfig.direction === 'asc' ? 1 : -1;
        
        // Convertir a números si son campos numéricos
        const isNumeric = ['clt_prm_caj', 'clt_prm_val_caj'].includes(sortConfig.key);
        if (isNumeric) {
          aValue = parseFloat(aValue) || 0;
          bValue = parseFloat(bValue) || 0;
          return sortConfig.direction === 'asc' ? aValue - bValue : bValue - aValue;
        }
        
        // Ordenamiento para cadenas de texto
        if (aValue < bValue) {
          return sortConfig.direction === 'asc' ? -1 : 1;
        }
        if (aValue > bValue) {
          return sortConfig.direction === 'asc' ? 1 : -1;
        }
        return 0;
      });
    }
    
    return sortableItems;
  }, [filteredClientes, sortConfig]);
  
  // Calcular agrupación y orden de aparición por código único
  const gruposPorCodigo = useMemo(() => {
    const map = new Map();
    const order = [];
    clientes.forEach((c, idx) => {
      const codigo = c.clt_codigo;
      if (!map.has(codigo)) {
        map.set(codigo, []);
        order.push(codigo);
      }
      map.get(codigo).push({ item: c, index: idx });
    });
    return { map, order };
  }, [clientes]);

  // Clientes únicos: totales, sin coordenadas y lista única con coordenadas para distancia
  const totalClientesUnicos = gruposPorCodigo.order.length;

  const clientesSinCoordenadasUnicos = useMemo(() => {
    return gruposPorCodigo.order.reduce((acc, codigo) => {
      const grupo = gruposPorCodigo.map.get(codigo) || [];
      const algunoConCoord = grupo.some(g => tieneCoordenadasValidas(g.item));
      return acc + (algunoConCoord ? 0 : 1);
    }, 0);
  }, [gruposPorCodigo]);

  const clientesUnicosConCoordenadasEnOrden = useMemo(() => {
    return gruposPorCodigo.order.map(codigo => {
      const grupo = gruposPorCodigo.map.get(codigo) || [];
      // Elegir la primera instancia con coordenadas válidas (según orden original)
      const conCoord = grupo
        .filter(g => tieneCoordenadasValidas(g.item))
        .sort((a, b) => a.index - b.index)[0];
      return conCoord ? conCoord.item : null;
    }).filter(Boolean);
  }, [gruposPorCodigo]);

  // Función para manejar el cambio de ordenamiento
  const requestSort = (key) => {
    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
  };
  
  // Calcular estadísticas generales
  const distanciaTotal = calcularDistanciaTotal(clientesUnicosConCoordenadasEnOrden);
  const sumaCajas = calcularSuma(clientes, 'clt_prm_caj');
  const sumaValorCajas = calcularSumaValCaj(clientes, 'clt_prm_val_caj');
  
  // Calcular clientes sin coordenadas
  const clientesSinCoordenadas = clientesSinCoordenadasUnicos;
  
  return (
    <ModalOverlay onClick={onClose}>
      <ModalContent onClick={e => e.stopPropagation()}>
        <ModalHeader>
          <ModalTitle>Detalle de Clientes</ModalTitle>
          <CloseButton onClick={onClose}>&times;</CloseButton>
        </ModalHeader>
        <ModalBody>
          <SummaryBox>
            <SummaryTitle>Resumen de Clientes</SummaryTitle>
            <SummaryCardsContainer>
              <SummaryCard color="#3498db">
                <CardIcon color="#3498db">💼</CardIcon>
                <CardLabel>Total de clientes</CardLabel>
                <CardValue>{totalClientesUnicos}</CardValue>
              </SummaryCard>
              
              <SummaryCard color="#e74c3c">
                <CardIcon color="#e74c3c">📍</CardIcon>
                <CardLabel>Sin coordenadas</CardLabel>
                <CardValue>{clientesSinCoordenadas}</CardValue>
              </SummaryCard>
              
              <SummaryCard color="#2ecc71">
                <CardIcon color="#2ecc71">🚩</CardIcon>
                <CardLabel>Distancia total</CardLabel>
                <CardValue>{distanciaTotal.toFixed(2)} km</CardValue>
              </SummaryCard>
              
              <SummaryCard color="#f39c12">
                <CardIcon color="#f39c12">💰</CardIcon>
                <CardLabel>Prom. cajas vendidas</CardLabel>
                <CardValue>{formatoNumero(sumaCajas)}</CardValue>
              </SummaryCard>
              
              <SummaryCard color="#9b59b6">
                <CardIcon color="#9b59b6">💲</CardIcon>
                <CardLabel>Prom. valor caja</CardLabel>
                <CardValue>{formatoMoneda(sumaValorCajas)}</CardValue>
              </SummaryCard>
            </SummaryCardsContainer>
          </SummaryBox>
          
          <SearchContainer>
            <SearchInput
              type="text"
              placeholder="Buscar clientes..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
            <span>{sortedClientes.length} clientes encontrados</span>
          </SearchContainer>
          
          <TableContainer>
            <Table>
              <thead>
                <tr>
                  <Th>
                    Código
                  </Th>
                  <Th>
                    Nombre
                  </Th>
                  <Th>
                    Vendedor
                  </Th>
                  <Th>
                    Día Visita
                  </Th>
                  <Th>
                    Frecuencia
                  </Th>
                  <Th>
                    Semana Visita
                  </Th>
                  <SortableTh 
                    onClick={() => requestSort('clt_prm_caj')}
                    $sortDirection={sortConfig.key === 'clt_prm_caj' ? sortConfig.direction : null}
                  >
                    Prom. Cajas
                  </SortableTh>
                  <SortableTh 
                    onClick={() => requestSort('clt_prm_val_caj')}
                    $sortDirection={sortConfig.key === 'clt_prm_val_caj' ? sortConfig.direction : null}
                  >
                    Prom. Valor Caja
                  </SortableTh>
                  <Th>
                    Coordenadas
                  </Th>
                </tr>
              </thead>
              <tbody>
                {sortedClientes.map((cliente) => (
                  <tr key={`${cliente.clt_codigo}__${cliente.clt_ven_codigo || 'NOVEN'}`}>
                    <Td>{cliente.clt_codigo}</Td>
                    <Td>{cliente.clt_nombre}</Td>
                    <Td>{cliente.clt_ven_codigo}</Td>
                    <Td>{cliente.clt_diavisita || 'N/A'}</Td>
                    <Td>{cliente.clt_frecuencia || 'N/A'}</Td>
                    <Td>{cliente.clt_semanavisita || 'N/A'}</Td>
                    <Td>{formatoNumero(cliente.clt_prm_caj)}</Td>
                    <Td>{formatoMoneda(cliente.clt_prm_val_caj)}</Td>
                    <Td style={{ color: cliente.clt_lat && cliente.clt_lon && cliente.clt_lat !== "" && cliente.clt_lon !== "" ? '#28a745' : '#dc3545' }}>
                      {cliente.clt_lat && cliente.clt_lon && cliente.clt_lat !== "" && cliente.clt_lon !== "" ? '✓ Correcta' : '✗ Sin coordenadas'}
                    </Td>
                  </tr>
                ))}
                {sortedClientes.length === 0 && (
                  <tr>
                    <Td colSpan="9" style={{ textAlign: 'center', padding: '1rem' }}>
                      No se encontraron clientes que coincidan con la búsqueda
                    </Td>
                  </tr>
                )}
              </tbody>
            </Table>
          </TableContainer>
        </ModalBody>
      </ModalContent>
    </ModalOverlay>
  );
};

export default DetailModal;
